import React, { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';

// Google Apps Script endpoint for form submissions
const FORM_ENDPOINT = 'https://script.google.com/macros/s/AKfycby4sRPHHXrqpES5l8OhdWduDzPjJW9JDPKw1erc_fjCtBAhwk_qgWcdOf-DQ3GxXy6wiQ/exec';

// Simple Capability Card
const CapabilityCard = ({ title, description, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.5, delay }}
    className="border-t border-stone-200 pt-6"
  >
    <h3 className="text-lg font-medium text-stone-900 mb-2">{title}</h3>
    <p className="text-stone-500 leading-relaxed">{description}</p>
  </motion.div>
);

const LandingPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedDate, setSelectedDate] = useState(8);
  const [weekOffset, setWeekOffset] = useState(0);
  const [hasSupplierNotifications, setHasSupplierNotifications] = useState(false);
  const [newSupplierAlert, setNewSupplierAlert] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [recentOrders, setRecentOrders] = useState([
    { id: "ORD-2847", supplier: "Cardinal Health", status: "In Transit", eta: "2 days" },
    { id: "ORD-2846", supplier: "McKesson", status: "Processing", eta: "4 days" },
    { id: "ORD-2845", supplier: "Medline", status: "Delivered", eta: "Completed" },
    { id: "ORD-2844", supplier: "Henry Schein", status: "In Transit", eta: "3 days" },
  ]);
  const [roomUtilization, setRoomUtilization] = useState([
    { room: "Room A1", usage: 85, provider: "Dr. Chen", status: "normal" },
    { room: "Room B2", usage: 92, provider: "Dr. Martinez", status: "normal" },
    { room: "Room A3", usage: 78, provider: "Dr. Williams", status: "normal" },
    { room: "Room C1", usage: 65, provider: "Dr. Patel", status: "normal" },
    { room: "Room D1", usage: 72, provider: "Dr. Kumar", status: "normal" },
    { room: "Room A2", usage: 88, provider: "Dr. Lee", status: "normal" },
    { room: "Room B1", usage: 75, provider: "Dr. Martinez", status: "normal" },
    { room: "Room C2", usage: 82, provider: "Dr. Chen", status: "normal" },
    { room: "Room D2", usage: 68, provider: "Dr. Williams", status: "normal" },
    { room: "Room A4", usage: 91, provider: "Dr. Patel", status: "normal" },
  ]);
  const scrollLockTimerRef = useRef(null);
  const isScrollLockedRef = useRef(false);
  const [isDemoEngaged, setIsDemoEngaged] = useState(false);

  const lockScroll = useCallback(() => {
    if (isScrollLockedRef.current) return;
    isScrollLockedRef.current = true;
    setIsDemoEngaged(true);
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    const navbar = document.querySelector('nav');
    if (navbar) navbar.style.paddingRight = `${scrollbarWidth}px`;
  }, []);

  const unlockScroll = useCallback(() => {
    setIsDemoEngaged(false);
    if (!isScrollLockedRef.current) return;
    isScrollLockedRef.current = false;
    document.body.style.overflow = 'auto';
    document.body.style.paddingRight = '0px';
    const navbar = document.querySelector('nav');
    if (navbar) navbar.style.paddingRight = '0px';
  }, []);

  const [formData, setFormData] = useState({
    email: '',
    clinicName: '',
    role: '',
    teamSize: '',
    biggestChallenge: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cleanup scroll lock on unmount and when switching to mobile (demo hidden)
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        if (scrollLockTimerRef.current) {
          clearTimeout(scrollLockTimerRef.current);
          scrollLockTimerRef.current = null;
        }
        unlockScroll();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (scrollLockTimerRef.current) {
        clearTimeout(scrollLockTimerRef.current);
        scrollLockTimerRef.current = null;
      }
      unlockScroll();
    };
  }, [unlockScroll]);

  // Initial supplier notification - starts after 1.5 seconds
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setHasSupplierNotifications(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  // Real-time supplier alerts and order updates
  React.useEffect(() => {
    if (activeTab !== 'suppliers') {
      const timer = setTimeout(() => {
        setHasSupplierNotifications(true);
        setNewSupplierAlert({
          item: "N95 Masks",
          supplier: "Cardinal Health",
          stock: 85,
          threshold: 200,
          status: "critical"
        });
      }, 2000); // New alert after 2 seconds

      return () => clearTimeout(timer);
    } else {
      // When in suppliers tab, update recent orders periodically
      const interval = setInterval(() => {
        setRecentOrders(prev => {
          const newOrder = {
            id: `ORD-${2848 + Math.floor(Math.random() * 100)}`,
            supplier: ["Cardinal Health", "McKesson", "Medline", "Henry Schein"][Math.floor(Math.random() * 4)],
            status: ["In Transit", "Processing"][Math.floor(Math.random() * 2)],
            eta: `${Math.floor(Math.random() * 5) + 1} days`
          };
          return [newOrder, ...prev.slice(0, 3)];
        });
      }, 4000); // New order every 4 seconds

      return () => clearInterval(interval);
    }
  }, [activeTab]);

  // Real-time room utilization updates (Dashboard tab) - slower updates with occasional alerts
  React.useEffect(() => {
    if (activeTab === 'dashboard') {
      const interval = setInterval(() => {
        setRoomUtilization(prev => 
          prev.map((room, index) => {
            const change = Math.random() > 0.5 ? 3 : -3;
            const newUsage = Math.min(98, Math.max(55, room.usage + change));
            
            // Occasionally mark rooms with special status (20% chance)
            let newStatus = 'normal';
            if (Math.random() < 0.2) {
              const statuses = ['alert', 'maintenance', 'normal'];
              newStatus = statuses[Math.floor(Math.random() * statuses.length)];
            }
            
            return {
              ...room,
              usage: newUsage,
              status: newStatus
            };
          })
        );
      }, 6000); // Update every 6 seconds (slower)

      return () => clearInterval(interval);
    }
  }, [activeTab]);

  // Appointment data per date - covering full January 2026
  const appointmentsByDate = {
    1: [
      { time: "9:00", patient: "Patricia R.", provider: "Dr. Chen", type: "Checkup" },
      { time: "10:00", patient: "Andrew M.", provider: "Dr. Martinez", type: "Follow-up" },
      { time: "11:00", patient: "Nicole H.", provider: "Dr. Williams", type: "Lab Review" },
      { time: "14:00", patient: "Tyler B.", provider: "Dr. Patel", type: "Consultation" },
    ],
    2: [
      { time: "9:30", patient: "Brandon K.", provider: "Dr. Kumar", type: "Therapy" },
      { time: "10:30", patient: "Samantha J.", provider: "Dr. Lee", type: "Physical" },
      { time: "13:00", patient: "Jordan T.", provider: "Dr. Chen", type: "Screening" },
      { time: "14:30", patient: "Rachel N.", provider: "Dr. Martinez", type: "Checkup" },
      { time: "15:30", patient: "Austin P.", provider: "Dr. Williams", type: "Follow-up" },
    ],
    3: [
      { time: "8:30", patient: "Heather S.", provider: "Dr. Patel", type: "Early Screening" },
      { time: "9:00", patient: "Dylan F.", provider: "Dr. Kumar", type: "Consultation" },
      { time: "10:00", patient: "Victoria W.", provider: "Dr. Lee", type: "Lab Review" },
      { time: "11:00", patient: "Nathan G.", provider: "Dr. Chen", type: "Checkup" },
      { time: "14:00", patient: "Alexis D.", provider: "Dr. Martinez", type: "Physical" },
    ],
    4: [
      { time: "9:00", patient: "Zachary L.", provider: "Dr. Williams", type: "Follow-up" },
      { time: "10:00", patient: "Megan R.", provider: "Dr. Patel", type: "Therapy" },
      { time: "13:30", patient: "Ethan M.", provider: "Dr. Kumar", type: "Checkup" },
      { time: "14:30", patient: "Stephanie C.", provider: "Dr. Lee", type: "Lab Review" },
      { time: "16:00", patient: "Kyle H.", provider: "Dr. Chen", type: "Consultation" },
    ],
    5: [
      { time: "8:00", patient: "Olivia T.", provider: "Dr. Martinez", type: "Early Checkup" },
      { time: "9:30", patient: "Aaron B.", provider: "Dr. Williams", type: "Physical" },
      { time: "10:30", patient: "Hannah W.", provider: "Dr. Patel", type: "Screening" },
      { time: "13:00", patient: "Ian K.", provider: "Dr. Kumar", type: "Follow-up" },
      { time: "14:00", patient: "Emma S.", provider: "Dr. Lee", type: "Lab Review" },
      { time: "15:00", patient: "Justin P.", provider: "Dr. Chen", type: "Therapy" },
    ],
    6: [
      { time: "9:00", patient: "Robert K.", provider: "Dr. Martinez", type: "Physical" },
      { time: "9:30", patient: "Amanda S.", provider: "Dr. Chen", type: "Checkup" },
      { time: "10:30", patient: "Kevin P.", provider: "Dr. Williams", type: "Lab Review" },
      { time: "14:00", patient: "Nancy W.", provider: "Dr. Kumar", type: "Follow-up" },
      { time: "15:00", patient: "Brian M.", provider: "Dr. Patel", type: "Consultation" },
    ],
    7: [
      { time: "8:30", patient: "Jennifer L.", provider: "Dr. Lee", type: "Screening" },
      { time: "9:00", patient: "Michael B.", provider: "Dr. Chen", type: "Therapy" },
      { time: "10:00", patient: "Patricia H.", provider: "Dr. Martinez", type: "Follow-up" },
      { time: "11:00", patient: "Daniel R.", provider: "Dr. Williams", type: "Consultation" },
      { time: "13:30", patient: "Linda G.", provider: "Dr. Patel", type: "Checkup" },
      { time: "14:30", patient: "Steven C.", provider: "Dr. Kumar", type: "Lab Review" },
      { time: "15:30", patient: "Carol T.", provider: "Dr. Lee", type: "Physical" },
    ],
    8: [
      { time: "9:00", patient: "Sarah M.", provider: "Dr. Chen", type: "Checkup" },
      { time: "9:30", patient: "James K.", provider: "Dr. Martinez", type: "Follow-up" },
      { time: "10:00", patient: "Emily R.", provider: "Dr. Williams", type: "Consultation" },
      { time: "10:30", patient: "David L.", provider: "Dr. Patel", type: "Lab Review" },
      { time: "11:00", patient: "Lisa P.", provider: "Dr. Chen", type: "Checkup" },
      { time: "11:30", patient: "Mark T.", provider: "Dr. Kumar", type: "Therapy" },
      { time: "14:00", patient: "Anna B.", provider: "Dr. Lee", type: "Follow-up" },
      { time: "14:30", patient: "Tom R.", provider: "Dr. Chen", type: "Consultation" },
      { time: "15:00", patient: "Rachel S.", provider: "Dr. Martinez", type: "Physical" },
      { time: "15:30", patient: "Chris D.", provider: "Dr. Williams", type: "Screening" },
      { time: "16:00", patient: "Michelle K.", provider: "Dr. Patel", type: "Checkup" },
      { time: "16:30", patient: "Jason P.", provider: "Dr. Kumar", type: "Lab Review" },
    ],
    9: [
      { time: "8:00", patient: "Elizabeth M.", provider: "Dr. Lee", type: "Early Screening" },
      { time: "8:30", patient: "William H.", provider: "Dr. Chen", type: "Checkup" },
      { time: "9:00", patient: "Barbara N.", provider: "Dr. Martinez", type: "Consultation" },
      { time: "9:30", patient: "Richard T.", provider: "Dr. Williams", type: "Follow-up" },
      { time: "10:00", patient: "Susan G.", provider: "Dr. Patel", type: "Lab Review" },
      { time: "10:30", patient: "Joseph A.", provider: "Dr. Kumar", type: "Therapy" },
      { time: "11:00", patient: "Karen W.", provider: "Dr. Lee", type: "Physical" },
      { time: "13:00", patient: "Thomas B.", provider: "Dr. Chen", type: "Checkup" },
      { time: "13:30", patient: "Jessica V.", provider: "Dr. Martinez", type: "Consultation" },
      { time: "14:00", patient: "Charles F.", provider: "Dr. Williams", type: "Follow-up" },
      { time: "14:30", patient: "Dorothy S.", provider: "Dr. Patel", type: "Screening" },
      { time: "15:00", patient: "Christopher E.", provider: "Dr. Kumar", type: "Lab Review" },
      { time: "15:30", patient: "Margaret L.", provider: "Dr. Lee", type: "Checkup" },
      { time: "16:00", patient: "Daniel K.", provider: "Dr. Chen", type: "Physical" },
      { time: "16:30", patient: "Helen R.", provider: "Dr. Martinez", type: "Therapy" },
    ],
    10: [
      { time: "9:00", patient: "Paul J.", provider: "Dr. Williams", type: "Consultation" },
      { time: "10:00", patient: "Betty D.", provider: "Dr. Chen", type: "Checkup" },
      { time: "11:00", patient: "Donald M.", provider: "Dr. Patel", type: "Lab Review" },
      { time: "13:30", patient: "Sandra H.", provider: "Dr. Martinez", type: "Follow-up" },
      { time: "14:30", patient: "Kenneth S.", provider: "Dr. Kumar", type: "Therapy" },
      { time: "15:30", patient: "Ashley P.", provider: "Dr. Lee", type: "Physical" },
      { time: "16:00", patient: "George C.", provider: "Dr. Williams", type: "Screening" },
      { time: "16:30", patient: "Donna B.", provider: "Dr. Chen", type: "Checkup" },
    ],
    11: [
      { time: "9:30", patient: "Edward N.", provider: "Dr. Martinez", type: "Follow-up" },
      { time: "10:30", patient: "Carol F.", provider: "Dr. Williams", type: "Consultation" },
      { time: "11:30", patient: "Ronald G.", provider: "Dr. Patel", type: "Lab Review" },
      { time: "14:00", patient: "Michelle T.", provider: "Dr. Chen", type: "Checkup" },
      { time: "15:00", patient: "Larry W.", provider: "Dr. Kumar", type: "Therapy" },
      { time: "16:00", patient: "Kimberly V.", provider: "Dr. Lee", type: "Physical" },
    ],
    12: [
      { time: "8:30", patient: "Matthew L.", provider: "Dr. Chen", type: "Early Checkup" },
      { time: "9:00", patient: "Sharon K.", provider: "Dr. Martinez", type: "Screening" },
      { time: "10:00", patient: "Gary R.", provider: "Dr. Williams", type: "Consultation" },
      { time: "11:00", patient: "Deborah M.", provider: "Dr. Patel", type: "Follow-up" },
      { time: "13:30", patient: "Frank H.", provider: "Dr. Kumar", type: "Lab Review" },
      { time: "14:30", patient: "Laura S.", provider: "Dr. Lee", type: "Therapy" },
      { time: "15:30", patient: "Raymond T.", provider: "Dr. Chen", type: "Physical" },
      { time: "16:00", patient: "Cynthia P.", provider: "Dr. Martinez", type: "Checkup" },
      { time: "16:30", patient: "Gregory B.", provider: "Dr. Williams", type: "Screening" },
      { time: "17:00", patient: "Angela N.", provider: "Dr. Patel", type: "Follow-up" },
    ],
    13: [
      { time: "9:00", patient: "Scott W.", provider: "Dr. Kumar", type: "Checkup" },
      { time: "10:00", patient: "Melissa P.", provider: "Dr. Lee", type: "Physical" },
      { time: "11:00", patient: "Vincent S.", provider: "Dr. Chen", type: "Lab Review" },
      { time: "14:00", patient: "Carolyn H.", provider: "Dr. Martinez", type: "Consultation" },
      { time: "15:00", patient: "Jacob R.", provider: "Dr. Williams", type: "Follow-up" },
    ],
    14: [
      { time: "8:30", patient: "Brittany K.", provider: "Dr. Patel", type: "Early Screening" },
      { time: "9:30", patient: "Roy M.", provider: "Dr. Kumar", type: "Therapy" },
      { time: "10:30", patient: "Gloria T.", provider: "Dr. Lee", type: "Checkup" },
      { time: "13:00", patient: "Eugene B.", provider: "Dr. Chen", type: "Physical" },
      { time: "14:30", patient: "Kathryn L.", provider: "Dr. Martinez", type: "Lab Review" },
      { time: "15:30", patient: "Ralph D.", provider: "Dr. Williams", type: "Consultation" },
    ],
    15: [
      { time: "9:00", patient: "Theresa C.", provider: "Dr. Patel", type: "Follow-up" },
      { time: "10:00", patient: "Arthur G.", provider: "Dr. Kumar", type: "Screening" },
      { time: "11:00", patient: "Julie N.", provider: "Dr. Lee", type: "Checkup" },
      { time: "13:30", patient: "Russell F.", provider: "Dr. Chen", type: "Lab Review" },
      { time: "14:30", patient: "Denise V.", provider: "Dr. Martinez", type: "Physical" },
      { time: "16:00", patient: "Gerald W.", provider: "Dr. Williams", type: "Therapy" },
    ],
    16: [
      { time: "8:00", patient: "Cheryl A.", provider: "Dr. Patel", type: "Early Checkup" },
      { time: "9:00", patient: "Albert H.", provider: "Dr. Kumar", type: "Consultation" },
      { time: "10:00", patient: "Mildred K.", provider: "Dr. Lee", type: "Follow-up" },
      { time: "11:00", patient: "Willie S.", provider: "Dr. Chen", type: "Lab Review" },
      { time: "14:00", patient: "Teresa R.", provider: "Dr. Martinez", type: "Physical" },
      { time: "15:00", patient: "Jesse M.", provider: "Dr. Williams", type: "Screening" },
      { time: "16:00", patient: "Judy P.", provider: "Dr. Patel", type: "Checkup" },
    ],
    17: [
      { time: "9:30", patient: "Billy T.", provider: "Dr. Kumar", type: "Therapy" },
      { time: "10:30", patient: "Marilyn B.", provider: "Dr. Lee", type: "Physical" },
      { time: "13:00", patient: "Craig L.", provider: "Dr. Chen", type: "Consultation" },
      { time: "14:00", patient: "Doris C.", provider: "Dr. Martinez", type: "Checkup" },
      { time: "15:00", patient: "Bruce D.", provider: "Dr. Williams", type: "Lab Review" },
    ],
    18: [
      { time: "8:30", patient: "Gloria W.", provider: "Dr. Patel", type: "Early Screening" },
      { time: "9:00", patient: "Roger N.", provider: "Dr. Kumar", type: "Follow-up" },
      { time: "10:00", patient: "Beverly G.", provider: "Dr. Lee", type: "Physical" },
      { time: "11:00", patient: "Carl F.", provider: "Dr. Chen", type: "Checkup" },
      { time: "13:30", patient: "Evelyn V.", provider: "Dr. Martinez", type: "Lab Review" },
      { time: "14:30", patient: "Harold S.", provider: "Dr. Williams", type: "Consultation" },
    ],
    19: [
      { time: "9:00", patient: "Diana E.", provider: "Dr. Patel", type: "Therapy" },
      { time: "10:00", patient: "Jeremy K.", provider: "Dr. Kumar", type: "Screening" },
      { time: "11:00", patient: "Frances H.", provider: "Dr. Lee", type: "Checkup" },
      { time: "14:00", patient: "Philip R.", provider: "Dr. Chen", type: "Physical" },
      { time: "15:00", patient: "Joan M.", provider: "Dr. Martinez", type: "Follow-up" },
      { time: "16:00", patient: "Randy T.", provider: "Dr. Williams", type: "Lab Review" },
    ],
    20: [
      { time: "8:00", patient: "Janice P.", provider: "Dr. Patel", type: "Early Checkup" },
      { time: "9:30", patient: "Howard B.", provider: "Dr. Kumar", type: "Consultation" },
      { time: "10:30", patient: "Kelly L.", provider: "Dr. Lee", type: "Physical" },
      { time: "13:00", patient: "Louis C.", provider: "Dr. Chen", type: "Screening" },
      { time: "14:00", patient: "Pamela D.", provider: "Dr. Martinez", type: "Checkup" },
      { time: "15:00", patient: "Terry W.", provider: "Dr. Williams", type: "Lab Review" },
      { time: "16:00", patient: "Joyce N.", provider: "Dr. Patel", type: "Follow-up" },
    ],
    21: [
      { time: "9:00", patient: "Adam G.", provider: "Dr. Kumar", type: "Therapy" },
      { time: "10:00", patient: "Norma F.", provider: "Dr. Lee", type: "Physical" },
      { time: "11:00", patient: "Sean V.", provider: "Dr. Chen", type: "Checkup" },
      { time: "13:30", patient: "Annie S.", provider: "Dr. Martinez", type: "Lab Review" },
      { time: "14:30", patient: "Alan E.", provider: "Dr. Williams", type: "Consultation" },
    ],
    22: [
      { time: "8:30", patient: "Katherine K.", provider: "Dr. Patel", type: "Early Screening" },
      { time: "9:00", patient: "Joe H.", provider: "Dr. Kumar", type: "Follow-up" },
      { time: "10:00", patient: "Irene R.", provider: "Dr. Lee", type: "Physical" },
      { time: "11:00", patient: "Harry M.", provider: "Dr. Chen", type: "Checkup" },
      { time: "14:00", patient: "Tammy T.", provider: "Dr. Martinez", type: "Therapy" },
      { time: "15:00", patient: "Carl P.", provider: "Dr. Williams", type: "Lab Review" },
    ],
    23: [
      { time: "9:00", patient: "Rose B.", provider: "Dr. Patel", type: "Screening" },
      { time: "10:00", patient: "Peter L.", provider: "Dr. Kumar", type: "Consultation" },
      { time: "11:00", patient: "Virginia C.", provider: "Dr. Lee", type: "Checkup" },
      { time: "13:30", patient: "Douglas D.", provider: "Dr. Chen", type: "Physical" },
      { time: "14:30", patient: "Ruby W.", provider: "Dr. Martinez", type: "Follow-up" },
      { time: "16:00", patient: "Wayne N.", provider: "Dr. Williams", type: "Lab Review" },
    ],
    24: [
      { time: "8:00", patient: "Lillian G.", provider: "Dr. Patel", type: "Early Checkup" },
      { time: "9:00", patient: "Johnny F.", provider: "Dr. Kumar", type: "Therapy" },
      { time: "10:00", patient: "Lois V.", provider: "Dr. Lee", type: "Physical" },
      { time: "11:00", patient: "Keith S.", provider: "Dr. Chen", type: "Screening" },
      { time: "14:00", patient: "Jean E.", provider: "Dr. Martinez", type: "Checkup" },
      { time: "15:00", patient: "Bryan K.", provider: "Dr. Williams", type: "Lab Review" },
    ],
    25: [
      { time: "9:30", patient: "Phyllis H.", provider: "Dr. Patel", type: "Consultation" },
      { time: "10:30", patient: "Eugene R.", provider: "Dr. Kumar", type: "Follow-up" },
      { time: "13:00", patient: "Bonnie M.", provider: "Dr. Lee", type: "Physical" },
      { time: "14:00", patient: "Jack T.", provider: "Dr. Chen", type: "Checkup" },
      { time: "15:00", patient: "Paula P.", provider: "Dr. Martinez", type: "Therapy" },
    ],
    26: [
      { time: "8:30", patient: "Fred B.", provider: "Dr. Williams", type: "Early Screening" },
      { time: "9:00", patient: "Diane L.", provider: "Dr. Patel", type: "Lab Review" },
      { time: "10:00", patient: "Victor C.", provider: "Dr. Kumar", type: "Physical" },
      { time: "11:00", patient: "Kathryn D.", provider: "Dr. Lee", type: "Checkup" },
      { time: "13:30", patient: "Samuel W.", provider: "Dr. Chen", type: "Consultation" },
      { time: "14:30", patient: "Marie N.", provider: "Dr. Martinez", type: "Follow-up" },
    ],
    27: [
      { time: "9:00", patient: "Larry G.", provider: "Dr. Williams", type: "Screening" },
      { time: "10:00", patient: "Martha F.", provider: "Dr. Patel", type: "Therapy" },
      { time: "11:00", patient: "Dennis V.", provider: "Dr. Kumar", type: "Checkup" },
      { time: "14:00", patient: "Grace S.", provider: "Dr. Lee", type: "Physical" },
      { time: "15:00", patient: "Jeffrey E.", provider: "Dr. Chen", type: "Lab Review" },
      { time: "16:00", patient: "Ruth K.", provider: "Dr. Martinez", type: "Consultation" },
    ],
    28: [
      { time: "8:00", patient: "Henry H.", provider: "Dr. Williams", type: "Early Checkup" },
      { time: "9:00", patient: "Christina R.", provider: "Dr. Patel", type: "Follow-up" },
      { time: "10:00", patient: "Patrick M.", provider: "Dr. Kumar", type: "Physical" },
      { time: "11:00", patient: "Judith T.", provider: "Dr. Lee", type: "Screening" },
      { time: "14:00", patient: "Gregory P.", provider: "Dr. Chen", type: "Checkup" },
      { time: "15:00", patient: "Shirley B.", provider: "Dr. Martinez", type: "Lab Review" },
      { time: "16:00", patient: "Dennis L.", provider: "Dr. Williams", type: "Therapy" },
    ],
    29: [
      { time: "9:30", patient: "Ann C.", provider: "Dr. Patel", type: "Consultation" },
      { time: "10:30", patient: "Jerry D.", provider: "Dr. Kumar", type: "Checkup" },
      { time: "13:00", patient: "Catherine W.", provider: "Dr. Lee", type: "Physical" },
      { time: "14:00", patient: "Frank N.", provider: "Dr. Chen", type: "Follow-up" },
      { time: "15:00", patient: "Helen G.", provider: "Dr. Martinez", type: "Lab Review" },
    ],
    30: [
      { time: "8:30", patient: "Walter F.", provider: "Dr. Williams", type: "Early Screening" },
      { time: "9:00", patient: "Martha V.", provider: "Dr. Patel", type: "Therapy" },
      { time: "10:00", patient: "Logan S.", provider: "Dr. Kumar", type: "Physical" },
      { time: "11:00", patient: "Debra E.", provider: "Dr. Lee", type: "Checkup" },
      { time: "13:30", patient: "Joshua K.", provider: "Dr. Chen", type: "Consultation" },
      { time: "14:30", patient: "Jacqueline H.", provider: "Dr. Martinez", type: "Lab Review" },
    ],
    31: [
      { time: "9:00", patient: "Gary R.", provider: "Dr. Williams", type: "Screening" },
      { time: "10:00", patient: "Frances M.", provider: "Dr. Patel", type: "Follow-up" },
      { time: "11:00", patient: "Lawrence T.", provider: "Dr. Kumar", type: "Checkup" },
      { time: "14:00", patient: "Virginia P.", provider: "Dr. Lee", type: "Physical" },
      { time: "15:00", patient: "Harold B.", provider: "Dr. Chen", type: "Lab Review" },
      { time: "16:00", patient: "Betty L.", provider: "Dr. Martinez", type: "Consultation" },
    ],
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (FORM_ENDPOINT) {
        // Using no-cors mode for Google Apps Script
        await fetch(FORM_ENDPOINT, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        
        // With no-cors, we can't read the response, but if no error is thrown,
        // assume success. Check your Google Sheet to verify!
        console.log('Form submitted! Check your Google Sheet and email.');
      } else {
        console.log('FORM_ENDPOINT not set:', formData);
      }
      setSubmitted(true);
    } catch (error) {
      console.error('Submission error:', error);
      alert('Something went wrong. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Navigation */}
      <motion.nav 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 left-0 right-0 z-50 border-b border-stone-200/50 backdrop-blur-xl bg-[#FAF9F6]/80"
      >
        <div className="w-full px-6 md:px-12 py-3.5">
          <div className="flex items-center justify-between w-full">
            <div className="text-lg font-medium tracking-[0.2em] text-stone-900">MILLOHEALTH</div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#capabilities" className="text-sm text-stone-500 hover:text-stone-900 transition-colors">Capabilities</a>
              <a href="#how-it-works" className="text-sm text-stone-500 hover:text-stone-900 transition-colors">How it works</a>
              <a href="#team" className="text-sm text-stone-500 hover:text-stone-900 transition-colors">Team</a>
              <a href="#contact" className="text-sm px-5 py-2 bg-stone-900 text-white rounded-full hover:bg-stone-800 transition-colors">
                Book a demo
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-stone-600 hover:text-stone-900 transition-colors"
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="md:hidden mt-4 pt-4 border-t border-stone-200/50 space-y-3"
            >
              <a href="#capabilities" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-stone-600 hover:text-stone-900 transition-colors py-2">
                Capabilities
              </a>
              <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-stone-600 hover:text-stone-900 transition-colors py-2">
                How it works
              </a>
              <a href="#team" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-stone-600 hover:text-stone-900 transition-colors py-2">
                Team
              </a>
              <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-center px-5 py-2 bg-stone-900 text-white rounded-full hover:bg-stone-800 transition-colors">
                Book a demo
              </a>
            </motion.div>
          )}
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden">
        {/* Hero Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url(/images/hero-background.jpg)' }}
        />
        {/* Subtle Background Gradient - Desktop */}
        <div className="absolute inset-0 overflow-hidden hidden lg:block">
          <div className="absolute w-[600px] h-[600px] bg-gradient-to-br from-[#2D5A5A]/15 to-transparent blur-3xl -top-40 -right-40 opacity-60" />
          <div className="absolute w-[500px] h-[500px] bg-gradient-to-br from-[#5C6B5C]/10 to-transparent blur-3xl bottom-20 -left-40 opacity-60" />
        </div>

        {/* Static Gradient - Mobile Only (more prominent) */}
        <div className="absolute inset-0 overflow-hidden lg:hidden">
          <div className="absolute w-[350px] h-[350px] bg-gradient-to-br from-[#2D5A5A]/30 to-transparent blur-3xl top-20 right-0 opacity-80" />
          <div className="absolute w-[300px] h-[300px] bg-gradient-to-br from-[#5C6B5C]/25 to-transparent blur-3xl bottom-32 -left-10 opacity-70" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-6xl mx-auto w-full px-8 min-h-screen flex flex-col">
          {/* Title: 1/3 from top, centered */}
          <div className="absolute top-[33%] left-0 right-0 px-8 lg:static lg:top-auto lg:flex-1 lg:flex lg:items-center lg:pt-32">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8 max-w-4xl mx-auto text-center lg:text-left"
            >
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-light text-stone-900 leading-[1.08] tracking-tight">
                The coordination layer for <span className="text-[#2D5A5A]">care access</span> and <span className="text-[#2D5A5A]">operations</span>.
              </h1>
            </motion.div>
          </div>

          {/* Bottom section with interactive dashboard - hidden on mobile */}
          <motion.div
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="pb-8 hidden lg:block"
            onMouseEnter={() => {
              scrollLockTimerRef.current = setTimeout(lockScroll, 2500);
            }}
            onMouseLeave={() => {
              if (scrollLockTimerRef.current) {
                clearTimeout(scrollLockTimerRef.current);
                scrollLockTimerRef.current = null;
              }
              unlockScroll();
            }}
          >
            <div
              onClick={() => {
                if (scrollLockTimerRef.current) {
                  clearTimeout(scrollLockTimerRef.current);
                  scrollLockTimerRef.current = null;
                }
                lockScroll();
              }}
              className="relative rounded-2xl overflow-hidden backdrop-blur-md border border-stone-200/50 shadow-xl"
            >
              {/* Animated background layer - opacity transitions smoothly (gradient class switch cannot) */}
              <div
                className="absolute inset-0 bg-gradient-to-br from-stone-100 to-stone-50 transition-opacity duration-[2.5s] ease-in-out"
                style={{ opacity: isDemoEngaged ? 0.55 : 0.2 }}
                aria-hidden
              />
              <div className="relative z-10 aspect-[16/9] md:aspect-[2.4/1] p-4 md:p-6">
                {/* Window chrome */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-400/60"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400/60"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400/60"></div>
                </div>

                {/* Content Grid */}
                <div className="grid grid-cols-12 gap-3 h-[calc(100%-32px)]">
                  {/* Left Sidebar with Tab Navigation */}
                  <div className="col-span-2 space-y-2 hidden md:flex flex-col bg-white/70 rounded-lg p-3">
                    {/* Tab Buttons */}
                    {[
                      { id: 'dashboard', label: 'Dashboard' },
                      { id: 'workflows', label: 'Workflows' },
                      { id: 'calendar', label: 'Calendar' },
                      { id: 'suppliers', label: 'Suppliers', hasNotification: hasSupplierNotifications }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => {
                          setActiveTab(tab.id);
                          if (tab.id === 'suppliers') setHasSupplierNotifications(false);
                        }}
                        className={`h-5 px-2 text-xs font-medium rounded flex items-center justify-between transition-all ${
                          activeTab === tab.id
                            ? 'bg-[#2D5A5A]/15 text-[#2D5A5A]'
                            : 'text-stone-500 hover:text-stone-700'
                        }`}
                      >
                        <span>{tab.label}</span>
                        {tab.hasNotification && activeTab !== tab.id && (
                          <div className="w-2 h-2 rounded-full bg-[#2D5A5A] animate-pulse"></div>
                        )}
                      </button>
                    ))}
                    
                    {/* Spacer */}
                    <div className="flex-1"></div>
                    
                    {/* Doctor Profile */}
                    <div className="pt-1.5 mt-1.5 border-t border-stone-200/50">
                      <div className="flex items-center justify-between px-1.5 py-1">
                        <div className="text-[9px] font-medium text-stone-900">Dr. Chen</div>
                        <div className="text-[8px] text-stone-500">Admin</div>
                      </div>
                    </div>
                  </div>

                  {/* Dashboard Tab */}
                  {activeTab === 'dashboard' && (
                    <motion.div
                      key="dashboard"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="col-span-12 md:col-span-10 space-y-3 overflow-hidden"
                    >
                      {/* Stats row */}
                      <div className="grid grid-cols-4 gap-2">
                        <div className="bg-white/70 rounded-lg p-2.5 shadow-sm">
                          <div className="text-[10px] text-stone-400 mb-0.5">Staff Today</div>
                          <div className="text-base font-semibold text-stone-900">24</div>
                          <div className="text-[9px] text-[#2D5A5A]">↑ 2 from yesterday</div>
                        </div>
                        <div className="bg-white/70 rounded-lg p-2.5 shadow-sm">
                          <div className="text-[10px] text-stone-400 mb-0.5">Appointments</div>
                          <div className="text-base font-semibold text-[#2D5A5A]">156</div>
                          <div className="text-[9px] text-stone-400">87% capacity</div>
                        </div>
                        <div className="bg-white/70 rounded-lg p-2.5 shadow-sm">
                          <div className="text-[10px] text-stone-400 mb-0.5">Referrals</div>
                          <div className="text-base font-semibold text-stone-900">12</div>
                          <div className="text-[9px] text-stone-400">3 pending</div>
                        </div>
                        <div className="bg-white/70 rounded-lg p-2.5 shadow-sm">
                          <div className="text-[10px] text-stone-400 mb-0.5">Wait Time</div>
                          <div className="text-base font-semibold text-stone-900">8m</div>
                          <div className="text-[9px] text-[#2D5A5A]">↓ below avg</div>
                        </div>
                      </div>

                      {/* Three column layout - matching sidebar height */}
                      <div className="grid grid-cols-3 gap-3">
                        {/* Today's Schedule */}
                        <div className="bg-white/70 rounded-xl p-3 shadow-sm">
                          <div className="text-[10px] text-stone-400 mb-2 font-medium">Today's Schedule</div>
                          <div className="space-y-1.5 max-h-36 overflow-y-auto scrollbar-thin">
                            {[
                              { time: "9:00", name: "Dr. Chen", room: "A1", status: "active" },
                              { time: "10:30", name: "Dr. Patel", room: "C1", status: "active" },
                              { time: "13:00", name: "Dr. Martinez", room: "B2", status: "pending" },
                              { time: "14:30", name: "Dr. Williams", room: "A3", status: "pending" },
                              { time: "16:00", name: "Dr. Kumar", room: "B1", status: "pending" },
                            ].map((item, i) => (
                              <div key={i} className="flex items-center gap-2 text-[11px]">
                                <span className="text-stone-400 font-mono w-8">{item.time}</span>
                                <div className={`w-1.5 h-1.5 rounded-full ${item.status === 'active' ? 'bg-[#2D5A5A]' : 'bg-stone-300'}`}></div>
                                <span className="text-stone-700 flex-1">{item.name}</span>
                                <span className="text-stone-400">{item.room}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Recent Activity */}
                        <div className="bg-white/70 rounded-xl p-3 shadow-sm">
                          <div className="text-[10px] text-stone-400 mb-2 font-medium">Recent Activity</div>
                          <div className="space-y-1.5 max-h-36 overflow-y-auto scrollbar-thin">
                            {[
                              { action: "Referral sent", detail: "Cardiology", time: "2m ago" },
                              { action: "Schedule updated", detail: "Room B2", time: "5m ago" },
                              { action: "Inventory alert", detail: "Gloves low", time: "12m ago" },
                              { action: "Patient checked in", detail: "Room A1", time: "15m ago" },
                              { action: "Referral sent", detail: "Neurology", time: "18m ago" },
                              { action: "Equipment serviced", detail: "Room C1", time: "25m ago" },
                              { action: "Supply delivered", detail: "Syringes", time: "32m ago" },
                              { action: "Schedule updated", detail: "Room A3", time: "38m ago" },
                              { action: "Patient checked out", detail: "Room B2", time: "45m ago" },
                            ].map((item, i) => (
                              <div key={i} className="flex items-center gap-2 text-[11px]">
                                <div className="w-1 h-1 rounded-full bg-[#2D5A5A]/40"></div>
                                <span className="text-stone-700 flex-1">{item.action}</span>
                                <span className="text-stone-400">{item.time}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Resource Utilization */}
                        <div className="bg-white/70 rounded-xl p-3 shadow-sm">
                          <div className="text-[10px] text-stone-400 mb-2 font-medium">
                            Resource Utilization
                            <span className="ml-2 inline-flex items-center gap-1">
                              <div className="w-1.5 h-1.5 rounded-full bg-[#2D5A5A] animate-pulse"></div>
                              <span className="text-[8px]">Live</span>
                            </span>
                          </div>
                          <div className="space-y-2 max-h-36 overflow-y-auto scrollbar-thin pr-1">
                            {roomUtilization.map((resource, i) => (
                              <motion.div
                                key={i}
                                initial={{ opacity: 1 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.3 }}
                              >
                                <div className="flex items-center justify-between text-[10px] mb-1">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-stone-700">{resource.room}</span>
                                    {resource.status === 'alert' && (
                                      <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        className="flex items-center gap-0.5 text-[8px] text-amber-600"
                                      >
                                        <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                      </motion.div>
                                    )}
                                    {resource.status === 'maintenance' && (
                                      <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        className="flex items-center gap-0.5 text-[8px] text-stone-500"
                                      >
                                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                      </motion.div>
                                    )}
                                  </div>
                                  <motion.span 
                                    key={`${resource.room}-${resource.usage}`}
                                    initial={{ scale: 1.2, color: '#2D5A5A', fontWeight: 600 }}
                                    animate={{ scale: 1, color: '#9ca3af', fontWeight: 400 }}
                                    transition={{ duration: 0.4 }}
                                    className="text-stone-400"
                                  >
                                    {Math.round(resource.usage)}%
                                  </motion.span>
                                </div>
                                <div className="w-full bg-stone-100 rounded-full h-1.5 overflow-hidden">
                                  <motion.div 
                                    className={`h-1.5 rounded-full ${
                                      resource.status === 'alert' ? 'bg-amber-500' :
                                      resource.status === 'maintenance' ? 'bg-stone-400' :
                                      'bg-[#2D5A5A]'
                                    }`}
                                    initial={{ width: `${resource.usage}%` }}
                                    animate={{ width: `${resource.usage}%` }}
                                    transition={{ duration: 0.6, ease: "easeInOut" }}
                                  ></motion.div>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Workflows Tab */}
                  {activeTab === 'workflows' && (
                    <motion.div
                      key="workflows"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="col-span-12 md:col-span-10 space-y-3 overflow-hidden"
                    >
                      {/* Active Workflows - Scrollable */}
                      <div className="bg-white/70 rounded-lg p-3 shadow-sm">
                        <div className="text-[10px] text-stone-400 mb-2 font-medium">Active Workflows</div>
                        <div className="grid grid-cols-3 gap-3 max-h-24 overflow-y-auto scrollbar-thin">
                          {[
                            {
                              name: "Referral Routing",
                              status: "Active",
                              trigger: "New referral received",
                              steps: 4
                            },
                            {
                              name: "Inventory Reorder",
                              status: "Active",
                              trigger: "Stock below threshold",
                              steps: 3
                            },
                            {
                              name: "Schedule Optimizer",
                              status: "Active",
                              trigger: "Daily at 6:00 AM",
                              steps: 5
                            },
                            {
                              name: "Patient Follow-up",
                              status: "Active",
                              trigger: "Post-appointment",
                              steps: 3
                            },
                            {
                              name: "Equipment Maintenance",
                              status: "Active",
                              trigger: "Weekly schedule",
                              steps: 4
                            },
                            {
                              name: "Billing Automation",
                              status: "Active",
                              trigger: "After visit complete",
                              steps: 6
                            },
                          ].map((workflow, i) => (
                            <div key={i} className="bg-stone-50 rounded-lg p-2.5 border border-stone-200/50">
                              <div className="flex items-center gap-2 mb-1.5">
                                <div className="w-2 h-2 rounded-full bg-[#2D5A5A]"></div>
                                <div className="text-[10px] font-medium text-stone-900">{workflow.name}</div>
                              </div>
                              <div className="text-[9px] text-stone-400 space-y-0.5">
                                <div>Trigger: {workflow.trigger}</div>
                                <div>{workflow.steps} steps</div>
                                <div className="flex items-center gap-1 text-[#2D5A5A] mt-1">
                                  <div className="w-1 h-1 rounded-full bg-[#2D5A5A]"></div>
                                  {workflow.status}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Workflow Builder Preview */}
                      <div className="bg-white/70 rounded-xl p-3 shadow-sm">
                        <div className="text-[10px] text-stone-400 mb-3 font-medium">Workflow: Referral Routing</div>
                        <div className="flex items-center gap-2">
                          {[
                            { label: "Receive", desc: "New referral" },
                            { label: "Analyze", desc: "Classify urgency" },
                            { label: "Match", desc: "Find specialist" },
                            { label: "Route", desc: "Send & notify" },
                          ].map((step, i) => (
                            <React.Fragment key={i}>
                              <div className="flex-1 bg-stone-50 rounded-lg p-2 text-center border border-stone-200/50">
                                <div className="text-[10px] font-medium text-stone-900">{step.label}</div>
                                <div className="text-[9px] text-stone-400 mt-0.5">{step.desc}</div>
                              </div>
                              {i < 3 && (
                                <div className="text-stone-300 text-xs">→</div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>

                      {/* Workflow Activity */}
                      <div className="bg-white/70 rounded-xl p-3 shadow-sm">
                        <div className="text-[10px] text-stone-400 mb-2 font-medium">Recent Activity</div>
                        <div className="space-y-1.5 max-h-14 overflow-y-auto scrollbar-thin">
                          {[
                            { workflow: "Referral Routing", time: "2m ago", status: "completed" },
                            { workflow: "Schedule Optimizer", time: "15m ago", status: "completed" },
                            { workflow: "Inventory Reorder", time: "1h ago", status: "completed" },
                          ].map((run, i) => (
                            <div key={i} className="flex items-center gap-2 text-[11px]">
                              <div className="w-1.5 h-1.5 rounded-full bg-[#2D5A5A]"></div>
                              <span className="text-stone-700 flex-1">{run.workflow}</span>
                              <span className="text-stone-400">{run.time}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Calendar Tab */}
                  {activeTab === 'calendar' && (
                    <motion.div
                      key="calendar"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="col-span-12 md:col-span-10 space-y-3 overflow-hidden"
                    >
                      {/* Calendar Header */}
                      <div className="bg-white/70 rounded-lg p-2.5 shadow-sm">
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-medium text-stone-900">
                            January 2026
                          </div>
                          <div className="flex gap-1">
                            <button 
                              onClick={() => {
                                // Don't go before week containing January 1st
                                if (weekOffset > -1) {
                                  setWeekOffset(weekOffset - 1);
                                }
                              }}
                              className="px-2 py-1 text-[10px] text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Previous week"
                              disabled={weekOffset <= -1}
                            >
                              ← Prev
                            </button>
                            <button 
                              onClick={() => {
                                // Don't go beyond week containing January 31st
                                if (weekOffset < 3) {
                                  setWeekOffset(weekOffset + 1);
                                }
                              }}
                              className="px-2 py-1 text-[10px] text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded transition disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Next week"
                              disabled={weekOffset >= 3}
                            >
                              Next →
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Week View with Appointment Previews */}
                      <div className="bg-white/70 rounded-lg p-3 shadow-sm">
                        <div className="grid grid-cols-7 gap-2 mb-2">
                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => (
                            <div key={i} className="text-[9px] text-center text-stone-400 font-medium">{day}</div>
                          ))}
                        </div>
                        <div className="grid grid-cols-7 gap-2">
                          {(() => {
                            // Calculate the week to display based on weekOffset
                            // Base week is [6, 7, 8, 9, 10, 11, 12] (Mon Jan 6 - Sun Jan 12)
                            const baseStartDate = 6;
                            const weekStartDate = baseStartDate + (weekOffset * 7);
                            const weekDates = Array.from({ length: 7 }, (_, i) => weekStartDate + i);
                            
                            return weekDates.map((date, i) => {
                              // Check if date is valid (1-31 for January)
                              const isValidDate = date >= 1 && date <= 31;
                              
                              if (!isValidDate) {
                                // Render empty placeholder for invalid dates
                                return (
                                  <div 
                                    key={i} 
                                    className="rounded-lg p-1.5 h-20 bg-stone-50/50 border border-transparent"
                                  />
                                );
                              }
                              
                              const isSelected = date === selectedDate;
                              const dayAppointments = appointmentsByDate[date] || [];
                              return (
                                <button
                                  key={i}
                                  onClick={() => setSelectedDate(date)}
                                  className={`rounded-lg p-1.5 transition-all text-left h-20 overflow-hidden ${
                                    isSelected 
                                      ? 'bg-[#2D5A5A]/10 border border-[#2D5A5A]/30 ring-2 ring-[#2D5A5A]/20' 
                                      : 'bg-stone-50 hover:bg-stone-100 border border-transparent'
                                  }`}
                                >
                                  <div className={`text-[10px] font-medium mb-1 ${isSelected ? 'text-[#2D5A5A]' : 'text-stone-700'}`}>
                                    {date}
                                  </div>
                                  <div className="space-y-0.5">
                                    {dayAppointments.slice(0, 3).map((apt, idx) => (
                                      <div key={idx} className="text-[8px] text-stone-500 truncate">
                                        {apt.time}
                                      </div>
                                    ))}
                                    {dayAppointments.length > 3 && (
                                      <div className="text-[7px] text-stone-400">
                                        +{dayAppointments.length - 3} more
                                      </div>
                                    )}
                                  </div>
                                </button>
                              );
                            });
                          })()}
                        </div>
                      </div>

                      {/* Selected Day Details */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white/70 rounded-lg p-3 shadow-sm rounded-b-xl">
                          <div className="text-[10px] text-stone-400 mb-2 font-medium">
                            January {selectedDate}, 2026 - Appointments ({appointmentsByDate[selectedDate]?.length || 0})
                          </div>
                          <div className="space-y-2 max-h-32 overflow-y-auto scrollbar-thin">
                            {(appointmentsByDate[selectedDate] || []).map((apt, i) => (
                              <div key={i} className="flex items-start gap-2">
                                <div className="text-[10px] font-mono text-stone-400 w-8">{apt.time}</div>
                                <div className="flex-1">
                                  <div className="text-[11px] text-stone-900">{apt.patient}</div>
                                  <div className="text-[9px] text-stone-400">{apt.provider} · {apt.type}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Week Summary */}
                        <div className="bg-white/70 rounded-lg p-3 shadow-sm rounded-b-xl">
                          <div className="text-[10px] text-stone-400 mb-2 font-medium">
                            Week Summary
                          </div>
                          <div className="space-y-2 max-h-32 overflow-y-auto scrollbar-thin">
                            {(() => {
                              // Calculate week statistics
                              const baseStartDate = 6 + (weekOffset * 7);
                              const weekDates = Array.from({ length: 7 }, (_, i) => baseStartDate + i);
                              const totalAppointments = weekDates.reduce((sum, date) => 
                                sum + (appointmentsByDate[date]?.length || 0), 0);
                              const avgPerDay = Math.round(totalAppointments / 7);
                              const busiest = weekDates.reduce((max, date) => {
                                const count = appointmentsByDate[date]?.length || 0;
                                return count > (appointmentsByDate[max]?.length || 0) ? date : max;
                              }, weekDates[0]);
                              
                              return (
                                <>
                                  <div className="flex items-center justify-between text-[11px] pb-2 border-b border-stone-100">
                                    <span className="text-stone-600">Total Appointments</span>
                                    <span className="font-semibold text-[#2D5A5A]">{totalAppointments}</span>
                                  </div>
                                  <div className="flex items-center justify-between text-[11px] pb-2 border-b border-stone-100">
                                    <span className="text-stone-600">Average per Day</span>
                                    <span className="font-semibold text-stone-900">{avgPerDay}</span>
                                  </div>
                                  <div className="flex items-center justify-between text-[11px] pb-2 border-b border-stone-100">
                                    <span className="text-stone-600">Busiest Day</span>
                                    <span className="font-semibold text-stone-900">Jan {busiest}</span>
                                  </div>
                                  <div className="flex items-center justify-between text-[11px]">
                                    <span className="text-stone-600">Providers Active</span>
                                    <span className="font-semibold text-stone-900">7</span>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Suppliers Tab */}
                  {activeTab === 'suppliers' && (
                    <motion.div
                      key="suppliers"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="col-span-12 md:col-span-10 space-y-3 overflow-y-auto max-h-[calc(100vh-300px)] scrollbar-thin"
                    >
                      {/* Alerts */}
                      <div className="grid grid-cols-3 gap-3">
                        {newSupplierAlert && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: -20 }}
                            animate={{ 
                              opacity: 1, 
                              scale: 1, 
                              y: 0,
                            }}
                            transition={{ 
                              duration: 0.5,
                              type: "spring",
                              stiffness: 200,
                              damping: 20
                            }}
                            className="bg-white/70 rounded-lg p-3 shadow-sm border-l-2 border-[#2D5A5A]"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="text-[11px] font-medium text-stone-900">{newSupplierAlert.item}</div>
                              <div className="flex items-center gap-1">
                                <motion.span 
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ delay: 0.3, type: "spring" }}
                                  className="text-[8px] text-white bg-[#2D5A5A] px-1.5 py-0.5 rounded font-medium"
                                >
                                  NEW
                                </motion.span>
                                <div className="w-2 h-2 rounded-full bg-[#2D5A5A] animate-pulse"></div>
                              </div>
                            </div>
                            <div className="text-[9px] text-stone-400 space-y-0.5">
                              <div>Supplier: {newSupplierAlert.supplier}</div>
                              <div>Stock: {newSupplierAlert.stock} units</div>
                              <div>Threshold: {newSupplierAlert.threshold} units</div>
                              <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.4 }}
                                className="mt-1.5 font-medium text-[#2D5A5A] flex items-center gap-1"
                              >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                Action needed
                              </motion.div>
                            </div>
                          </motion.div>
                        )}
                        {[
                          { item: "Examination Gloves", supplier: "Cardinal Health", stock: 120, threshold: 500, status: "critical", daysUntil: 2 },
                          { item: "Syringes (3mL)", supplier: "McKesson", stock: 280, threshold: 400, status: "warning", daysUntil: 5 },
                          { item: "Gauze Pads", supplier: "Medline", stock: 450, threshold: 300, status: "ok", daysUntil: null },
                        ].slice(0, newSupplierAlert ? 2 : 3).map((alert, i) => (
                          <div key={i} className={`bg-white/70 rounded-lg p-3 shadow-sm border-l-2 ${
                            alert.status === 'critical' ? 'border-[#2D5A5A]' : 
                            alert.status === 'warning' ? 'border-stone-400' : 
                            'border-stone-200'
                          }`}>
                            <div className="flex items-start justify-between mb-2">
                              <div className="text-[11px] font-medium text-stone-900">{alert.item}</div>
                              {alert.status === 'critical' && (
                                <div className="w-2 h-2 rounded-full bg-[#2D5A5A] animate-pulse"></div>
                              )}
                            </div>
                            <div className="text-[9px] text-stone-400 space-y-0.5">
                              <div>Supplier: {alert.supplier}</div>
                              <div>Stock: {alert.stock} units</div>
                              <div>Threshold: {alert.threshold} units</div>
                              {alert.daysUntil && (
                                <div className={`mt-1.5 font-medium ${alert.status === 'critical' ? 'text-[#2D5A5A]' : 'text-stone-600'}`}>
                                  Reorder in {alert.daysUntil} {alert.daysUntil === 1 ? 'day' : 'days'}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Supplier List */}
                      <div className="bg-white/70 rounded-lg p-3 shadow-sm">
                        <div className="text-[10px] text-stone-400 mb-3 font-medium">Active Suppliers</div>
                        <div className="space-y-2 max-h-32 overflow-y-auto scrollbar-thin">
                          {[
                            { 
                              name: "Cardinal Health", 
                              category: "Medical Supplies",
                              lastOrder: "3 days ago",
                              nextDelivery: "Jan 10",
                              reliability: 98,
                              items: 24
                            },
                            { 
                              name: "McKesson Medical-Surgical", 
                              category: "Equipment & Supplies",
                              lastOrder: "5 days ago",
                              nextDelivery: "Jan 12",
                              reliability: 95,
                              items: 18
                            },
                            { 
                              name: "Medline Industries", 
                              category: "Surgical Supplies",
                              lastOrder: "1 week ago",
                              nextDelivery: "Jan 15",
                              reliability: 97,
                              items: 31
                            },
                            { 
                              name: "Owens & Minor", 
                              category: "Medical Equipment",
                              lastOrder: "2 weeks ago",
                              nextDelivery: "Jan 18",
                              reliability: 93,
                              items: 12
                            },
                            { 
                              name: "Henry Schein Medical", 
                              category: "Practice Supplies",
                              lastOrder: "4 days ago",
                              nextDelivery: "Jan 11",
                              reliability: 96,
                              items: 27
                            },
                            { 
                              name: "Concordance Healthcare", 
                              category: "Pharmaceuticals",
                              lastOrder: "1 week ago",
                              nextDelivery: "Jan 14",
                              reliability: 94,
                              items: 15
                            },
                          ].map((supplier, i) => (
                            <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50 transition">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <div className="text-[11px] font-medium text-stone-900">{supplier.name}</div>
                                  <div className="text-[8px] text-stone-400">{supplier.items} items</div>
                                </div>
                                <div className="text-[9px] text-stone-400">{supplier.category}</div>
                              </div>
                              <div className="text-right">
                                <div className="text-[9px] text-stone-600 mb-0.5">Next: {supplier.nextDelivery}</div>
                                <div className="text-[8px] text-stone-400">{supplier.reliability}% reliable</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Order Tracking */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white/70 rounded-lg p-3 shadow-sm">
                          <div className="text-[10px] text-stone-400 mb-2 font-medium">
                            Recent Orders
                            <span className="ml-2 inline-flex items-center gap-1">
                              <div className="w-1.5 h-1.5 rounded-full bg-[#2D5A5A] animate-pulse"></div>
                              <span className="text-[8px]">Live</span>
                            </span>
                          </div>
                          <div className="space-y-1.5 max-h-20 overflow-hidden">
                            {recentOrders.map((order, i) => (
                              <motion.div
                                key={order.id}
                                initial={{ opacity: 0, x: -20, scale: 0.95 }}
                                animate={{ opacity: 1, x: 0, scale: 1 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.3, delay: i * 0.05 }}
                                className="flex items-center gap-2 text-[11px]"
                              >
                                <div className={`w-1.5 h-1.5 rounded-full ${
                                  order.status === 'Delivered' ? 'bg-stone-300' : 
                                  order.status === 'In Transit' ? 'bg-[#2D5A5A] animate-pulse' : 
                                  'bg-stone-400'
                                }`}></div>
                                <span className="text-stone-400 font-mono w-16 text-[10px]">{order.id}</span>
                                <span className="text-stone-700 flex-1 text-[10px]">{order.supplier}</span>
                                <span className="text-stone-400 text-[9px]">{order.eta}</span>
                              </motion.div>
                            ))}
                          </div>
                        </div>

                        <div className="bg-white/70 rounded-lg p-3 shadow-sm">
                          <div className="text-[10px] text-stone-400 mb-2 font-medium">Spending Overview</div>
                          <div className="space-y-2">
                            {[
                              { category: "Medical Supplies", amount: 12450, percent: 45 },
                              { category: "Equipment", amount: 8200, percent: 30 },
                              { category: "Pharmaceuticals", amount: 6850, percent: 25 },
                            ].map((item, i) => (
                              <div key={i}>
                                <div className="flex items-center justify-between text-[10px] mb-1">
                                  <span className="text-stone-700">{item.category}</span>
                                  <span className="text-stone-900 font-medium">${item.amount.toLocaleString()}</span>
                                </div>
                                <div className="w-full bg-stone-100 rounded-full h-1.5">
                                  <div 
                                    className="bg-[#2D5A5A] h-1.5 rounded-full transition-all"
                                    style={{ width: `${item.percent}%` }}
                                  ></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Message Section */}
      <section className="py-24 md:py-32 px-8 bg-white border-t border-stone-100">
        <div className="max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="text-2xl md:text-3xl lg:text-4xl font-light text-stone-800 leading-relaxed text-center"
          >
            <span className="text-[#2D5A5A] font-normal">millohealth</span> transforms manual care coordination workflows into seamless, automated systems.
          </motion.p>
        </div>
      </section>

      {/* Our Approach Section */}
      <section id="capabilities" className="py-20 md:py-24 px-8 bg-[#F5F4F0]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <span className="text-sm font-medium tracking-widest text-[#2D5A5A] uppercase mb-3 block">
              Our Approach
            </span>
            <h2 className="text-2xl md:text-3xl font-light text-stone-900 mb-4">
              Systems, not single applications
            </h2>
            <p className="text-stone-600 leading-relaxed max-w-3xl">
              Healthcare doesn't need another point solution. We build coordination systems that connect existing workflows—from initial patient contact through follow-up care—into intelligent, automated processes designed for clinical teams.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
            <CapabilityCard
              title="Care Coordination"
              description="Automate the back-and-forth between report submission, diagnosis, and follow-up scheduling. Eliminate manual handoffs."
              delay={0}
            />
            <CapabilityCard
              title="Workflow Integration"
              description="Connect your EHR, scheduling, and communication tools into unified workflows that mirror how your team actually works."
              delay={0.05}
            />
            <CapabilityCard
              title="Referral Management"
              description="Route referrals to the right specialists automatically. Track status and trigger follow-ups without manual intervention."
              delay={0.1}
            />
            <CapabilityCard
              title="Resource Optimization"
              description="Coordinate staff schedules, room utilization, and supply needs with real-time operational data."
              delay={0.15}
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 md:py-24 px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <span className="text-sm font-medium tracking-widest text-[#2D5A5A] uppercase mb-3 block">
              How it works
            </span>
            <h2 className="text-2xl md:text-3xl font-light text-stone-900">
              Integration to impact in weeks
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="space-y-8">
              {[
                { num: "1", title: "Connect", desc: "Integrates with your EHR and scheduling tools via secure APIs." },
                { num: "2", title: "Learn", desc: "We map your workflows, staff preferences, and compliance requirements." },
                { num: "3", title: "Configure", desc: "Automation rules tailored to how your clinic operates." },
                { num: "4", title: "Go live", desc: "Launch with dedicated support. Continuous improvement from feedback." }
              ].map((step, i) => (
                <motion.div 
                  key={i} 
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex gap-4"
                >
                  <div className="w-8 h-8 rounded-full bg-[#2D5A5A] text-white text-sm flex items-center justify-center flex-shrink-0">
                    {step.num}
                  </div>
                  <div>
                    <h3 className="font-medium text-stone-900 mb-1">{step.title}</h3>
                    <p className="text-stone-500 text-sm">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
              className="bg-[#F5F4F0] rounded-xl p-8 border border-stone-200"
            >
              <h3 className="font-medium text-stone-900 mb-4">Enterprise deployment</h3>
              <p className="text-sm text-stone-600 mb-6">
                We work directly with clinical administrators, practice managers, and hospital systems—not individual patients. Our model is built for institutional adoption.
              </p>
              <ul className="space-y-3 text-sm text-stone-600">
                {[
                  "HIPAA-compliant from day one",
                  "2-4 week implementation timeline",
                  "No disruption to patient care",
                  "Direct partnership with clinical leadership"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-[#2D5A5A] flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-20 md:py-24 px-8 bg-[#F5F4F0]">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-sm font-medium tracking-widest text-[#2D5A5A] uppercase mb-6 block">
              Team
            </span>
            
            <p className="text-xl md:text-2xl font-light text-stone-600 mb-12 leading-relaxed">
              Previously worked at Amazon, Stanford Health Care, and early-stage startups backed by Y Combinator.
            </p>

            {/* Company Logos */}
            <div className="flex items-center justify-center gap-12 md:gap-20">
              {/* Amazon */}
              <div className="opacity-70 hover:opacity-100 transition-opacity duration-300">
                <img src="/images/amazon-logo.svg" alt="Amazon" className="h-6 md:h-7 w-auto object-contain" />
              </div>

              {/* Stanford Health Care */}
              <div className="flex flex-col items-center gap-2 opacity-70 hover:opacity-100 transition-opacity duration-300">
                <img src="/images/stanford-healthcare-logo.png" alt="Stanford Health Care" className="h-10 md:h-12 w-auto object-contain" />
                <span className="text-xs text-stone-500 font-medium">Stanford Health Care</span>
              </div>

              {/* Auctor */}
              <div className="flex flex-col items-center gap-2 opacity-70 hover:opacity-100 transition-opacity duration-300">
                <img src="/images/auctor-logo.png" alt="Auctor" className="h-8 md:h-10 w-auto object-contain" />
                <span className="text-xs text-stone-500 font-medium">Auctor</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 md:py-24 px-8 bg-white">
        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <span className="text-sm font-medium tracking-widest text-[#2D5A5A] uppercase mb-3 block">
              Get started
            </span>
            <h2 className="text-2xl md:text-3xl font-light text-stone-900 mb-3">
              Schedule a conversation
            </h2>
            <p className="text-stone-500">
              Tell us about your clinic and we'll reach out.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5 }}
          >
          {submitted ? (
              <div className="text-center py-12 bg-[#F5F4F0] rounded-xl border border-stone-200">
                <div className="w-12 h-12 bg-[#2D5A5A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-[#2D5A5A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
                <h3 className="text-xl font-light text-stone-900 mb-2">Thank you</h3>
                <p className="text-stone-500 text-sm">We'll reach out within 1-2 business days.</p>
            </div>
          ) : (
              <form onSubmit={handleSubmit} className="bg-[#F5F4F0] rounded-xl border border-stone-200 p-6 md:p-8 space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm text-stone-600 mb-1.5">Work email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-white border border-stone-200 rounded-lg focus:border-[#2D5A5A] outline-none transition-all text-stone-900 placeholder-stone-400 text-sm"
                    placeholder="you@clinic.com"
                  />
                </div>

                <div>
                  <label htmlFor="clinicName" className="block text-sm text-stone-600 mb-1.5">Clinic name</label>
                  <input
                    type="text"
                    id="clinicName"
                    name="clinicName"
                    value={formData.clinicName}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-white border border-stone-200 rounded-lg focus:border-[#2D5A5A] outline-none transition-all text-stone-900 placeholder-stone-400 text-sm"
                    placeholder="Your clinic name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                <div>
                    <label htmlFor="role" className="block text-sm text-stone-600 mb-1.5">Role</label>
                  <input
                    type="text"
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                      className="w-full px-4 py-2.5 bg-white border border-stone-200 rounded-lg focus:border-[#2D5A5A] outline-none transition-all text-stone-900 placeholder-stone-400 text-sm"
                      placeholder="Your role"
                  />
                </div>
                <div>
                    <label htmlFor="teamSize" className="block text-sm text-stone-600 mb-1.5">Staff size</label>
                  <select
                    id="teamSize"
                    name="teamSize"
                    value={formData.teamSize}
                    onChange={handleChange}
                      className="w-full px-4 py-2.5 bg-white border border-stone-200 rounded-lg focus:border-[#2D5A5A] outline-none transition-all text-stone-900 text-sm"
                    >
                      <option value="">Select</option>
                      <option value="1-10">1-10</option>
                      <option value="11-25">11-25</option>
                      <option value="26-50">26-50</option>
                      <option value="51-100">51-100</option>
                      <option value="100+">100+</option>
                  </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="biggestChallenge" className="block text-sm text-stone-600 mb-1.5">Biggest challenge</label>
                  <textarea
                    id="biggestChallenge"
                    name="biggestChallenge"
                    rows={2}
                    value={formData.biggestChallenge}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 bg-white border border-stone-200 rounded-lg focus:border-[#2D5A5A] outline-none transition-all resize-none text-stone-900 placeholder-stone-400 text-sm"
                    placeholder="e.g., Scheduling takes too long..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#2D5A5A] text-white py-3 rounded-lg hover:bg-[#234848] transition-colors disabled:opacity-50 text-sm"
                >
                  {isSubmitting ? 'Submitting...' : 'Request a conversation'}
                </button>
            </form>
          )}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-8 border-t border-stone-200 bg-[#FAF9F6]">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm font-light tracking-[0.15em] text-stone-900">MILLOHEALTH</div>
            <div className="flex items-center gap-6 text-xs text-stone-500">
              <a href="mailto:team@millohealth.com" className="hover:text-stone-900 transition-colors">
                team@millohealth.com
              </a>
              <span>© 2026 MilloHealth</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
